// eslint-disable-next-line no-restricted-imports
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosError, AxiosResponse } from "axios";
import { EmailPreferencesResponse } from "types/generated/models";
import { useSnackbar } from "@powerdiary/components-library/lib";
import { useEffect } from "react";
import { showNetworkErrorSnackbar } from "utils/networkErrorSnackbar";

export const emailPreferencesUrl = (token: string) => `/api/v1/EmailPreferences/${encodeURIComponent(token)}`;

export interface IUseEmailPreferencesHook {
    data?: EmailPreferencesResponse;
    isLoading: boolean;
    // Unknown, forged or switched-off link: the API answers 404 without saying which.
    isNotFound: boolean;
}

// Anonymous page outside the portal shell, so it cannot use useGetQuery (see frontend-client-portal.md).
export function useEmailPreferences(token: string): IUseEmailPreferencesHook {
    const { enqueueSnackbar } = useSnackbar();
    const { data, isLoading, error } = useQuery<AxiosResponse<EmailPreferencesResponse>, AxiosError>({
        queryKey: ["/api/v1/EmailPreferences", token],
        queryFn: () => axios.get<EmailPreferencesResponse>(emailPreferencesUrl(token)),
        enabled: Boolean(token),
        retry: false,
    });

    const isNotFound = !token || error?.response?.status === 404;

    useEffect(() => {
        if (!error || isNotFound) {
            return;
        }

        if (error.request && !error.response) {
            showNetworkErrorSnackbar();
        } else {
            enqueueSnackbar("Please try again later.", {
                variant: "error",
                alertTitle: "Error loading your email preferences",
            });
        }
    }, [error, isNotFound, enqueueSnackbar]);

    return { data: data?.data, isLoading, isNotFound };
}
